import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Building2,
  Package,
  Layers,
  KeyRound,
  Plus,
  Check,
  ChevronDown,
  Network,
  Megaphone,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import Notification from "../../../components/Notification/Notification";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../apiService";
import { getRole } from "../../../utils/auth";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./ProductAccess.module.scss";

/* ===================== UTILS ===================== */
const toNumberArray = (value) =>
  Array.isArray(value)
    ? value.map((item) => Number(item)).filter((item) => !Number.isNaN(item))
    : [];

const normalizeIds = (ids) => toNumberArray(ids).sort((a, b) => a - b);

const areSameIds = (left, right) => {
  const first = normalizeIds(left);
  const second = normalizeIds(right);
  if (first.length !== second.length) return false;
  return first.every((item, index) => item === second[index]);
};

const ProductAccess = () => {
  const navigate = useNavigate();
  const isSuperAdmin = getRole() === "Internal";
  /* =========================
     STATE
  ========================= */
  const [organizations, setOrganizations] = useState([]);
  const [products, setProducts] = useState([]);
  const [assignedProducts, setAssignedProducts] = useState({});
  const [savedAssignedProducts, setSavedAssignedProducts] = useState({});

  /* ----- APIs (new) ----- */
  const [apiCategories, setApiCategories] = useState([]);
  const [assignedApis, setAssignedApis] = useState({});
  const [savedAssignedApis, setSavedAssignedApis] = useState({});
  const [openApiPopover, setOpenApiPopover] = useState(null); // { orgId, categoryId }
  const apiPopoverRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [savingOrgId, setSavingOrgId] = useState(null);
  const [search, setSearch] = useState("");

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  /* =========================
     HELPERS
  ========================= */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     API CALLS
  ========================= */
  const fetchAllOrganizations = async () => {
    const firstResponse = await apiGet(
      "user",
      "/organizations?page=1&limit=100",
    );

    if (firstResponse?.status !== "success") {
      throw new Error("Organizations fetch failed");
    }

    const firstData = firstResponse.data || {};
    const firstPageOrganizations = firstData.data || [];
    const pagination = firstData.pagination || {};
    const totalPages = pagination.totalPages || 1;

    if (totalPages <= 1) return firstPageOrganizations;

    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, index) => index + 2,
    );

    const remainingResponses = await Promise.all(
      remainingPages.map((page) =>
        apiGet("user", `/organizations?page=${page}&limit=100`),
      ),
    );

    return remainingResponses.reduce(
      (allOrganizations, response) => [
        ...allOrganizations,
        ...(response?.data?.data || []),
      ],
      firstPageOrganizations,
    );
  };

  const fetchProducts = async () => {
    const response = await apiGet("catalog", "/apimaster/products");
    if (!response?.status === "success") {
      throw new Error("Products fetch failed");
    }
    return response.data || [];
  };

  const fetchAssignedProducts = async (orgId) => {
    try {
      const response = await apiGet(
        "catalog",
        `/apimaster/products/assigned/${orgId}`,
      );
      if (response?.status === "success") {
        return toNumberArray(response.data);
      }
    } catch (error) {
      console.error("Assigned products fetch failed", error);
    }
    return [];
  };

  const fetchApiCategories = async () => {
    try {
      const response = await apiGet("catalog", "/apimaster/apis/all-grouped");
      if (response?.status === "success" && Array.isArray(response.data)) {
        return response.data;
      }
    } catch (error) {
      console.error("API categories fetch failed", error);
    }
    return [];
  };

  const fetchGrantedApis = async (orgId) => {
    try {
      const response = await apiGet(
        "catalog",
        `/apimaster/apis/granted/${orgId}`,
      );
      if (response?.status === "success" && Array.isArray(response.data)) {
        // Flatten category -> apiMasterIds
        return response.data.reduce(
          (acc, cat) => acc.concat(toNumberArray(cat.apiMasterIds)),
          [],
        );
      }
    } catch (error) {
      console.error("Granted APIs fetch failed", error);
    }
    return [];
  };

  const loadProductAccess = async () => {
    try {
      setLoading(true);

      const [organizationList, productList, apiCategoryList] =
        await Promise.all([
          fetchAllOrganizations(),
          fetchProducts(),
          fetchApiCategories(),
        ]);

      const [productAssignedEntries, apiAssignedEntries] = await Promise.all([
        Promise.all(
          organizationList.map(async (organization) => [
            organization.id,
            await fetchAssignedProducts(organization.id),
          ]),
        ),
        Promise.all(
          organizationList.map(async (organization) => [
            organization.id,
            await fetchGrantedApis(organization.id),
          ]),
        ),
      ]);

      const productMap = Object.fromEntries(productAssignedEntries);
      const apiMap = Object.fromEntries(apiAssignedEntries);

      setOrganizations(organizationList);
      setProducts(productList);
      setApiCategories(apiCategoryList);
      setAssignedProducts(productMap);
      setSavedAssignedProducts(productMap);
      setAssignedApis(apiMap);
      setSavedAssignedApis(apiMap);
    } catch (error) {
      console.error("Product access load failed", error);
      showNotification("Failed to load product access data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProductAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close API category dropdown on outside click / ESC
  useEffect(() => {
    if (!openApiPopover) return undefined;
    const handleClickOutside = (e) => {
      if (
        apiPopoverRef.current &&
        !apiPopoverRef.current.contains(e.target) &&
        !e.target.closest("[data-api-pill]")
      ) {
        setOpenApiPopover(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpenApiPopover(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [openApiPopover]);

  /* =========================
     COMPUTED DATA
  ========================= */
  const filteredOrganizations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return organizations;

    return organizations.filter((organization) => {
      const values = [
        organization.name,
        organization.email,
        organization.companyName,
        organization.mobile,
      ];
      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );
    });
  }, [organizations, search]);

  const totalAssigned = useMemo(
    () =>
      Object.values(savedAssignedProducts).reduce(
        (total, ids) => total + toNumberArray(ids).length,
        0,
      ),
    [savedAssignedProducts],
  );

  /* =========================
     ACTIONS
  ========================= */
  const toggleProduct = (orgId, productId) => {
    setAssignedProducts((prev) => {
      const currentIds = toNumberArray(prev[orgId]);
      const exists = currentIds.includes(productId);
      const nextIds = exists
        ? currentIds.filter((id) => id !== productId)
        : [...currentIds, productId];

      return {
        ...prev,
        [orgId]: nextIds,
      };
    });
  };

  const toggleApi = (orgId, apiId) => {
    setAssignedApis((prev) => {
      const currentIds = toNumberArray(prev[orgId]);
      const exists = currentIds.includes(apiId);
      const nextIds = exists
        ? currentIds.filter((id) => id !== apiId)
        : [...currentIds, apiId];

      return {
        ...prev,
        [orgId]: nextIds,
      };
    });
  };

  const isProductsDirty = (orgId) =>
    !areSameIds(assignedProducts[orgId], savedAssignedProducts[orgId]);

  const isApisDirty = (orgId) =>
    !areSameIds(assignedApis[orgId], savedAssignedApis[orgId]);

  const isOrganizationDirty = (orgId) =>
    isProductsDirty(orgId) || isApisDirty(orgId);

  const handleSave = async (organization) => {
    const productIds = normalizeIds(assignedProducts[organization.id]);
    const apiMasterIds = normalizeIds(assignedApis[organization.id]);

    const productsChanged = isProductsDirty(organization.id);
    const apisChanged = isApisDirty(organization.id);

    if (!productsChanged && !apisChanged) return;

    try {
      setSavingOrgId(organization.id);

      const calls = [];
      if (productsChanged) {
        calls.push(
          apiPost("catalog", "/apimaster/products/assign", {
            orgId: organization.id,
            productIds,
          }),
        );
      }
      if (apisChanged) {
        calls.push(
          apiPost("catalog", "/apimaster/apis/assign", {
            orgId: organization.id,
            apiMasterIds,
          }),
        );
      }

      const responses = await Promise.all(calls);

      const anyFailed = responses.some(
        (r) => r?.success === false || r?.status === "error",
      );
      if (anyFailed) throw new Error("Assignment failed");

      if (productsChanged) {
        setSavedAssignedProducts((prev) => ({
          ...prev,
          [organization.id]: productIds,
        }));
      }
      if (apisChanged) {
        setSavedAssignedApis((prev) => ({
          ...prev,
          [organization.id]: apiMasterIds,
        }));
      }

      showNotification("Product access updated successfully");
    } catch (error) {
      console.error("Assignment failed", error);
      showNotification("Failed to update product access", "error");
    } finally {
      setSavingOrgId(null);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-product-acc__page"]}>
      {notification && (
        <div className={styles["admin-product-acc__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-product-acc__header"]}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div className={styles["admin-product-acc__icon-wrapper"]}>
            <KeyRound size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-product-acc__title-group"]}>
            <h2 className={styles["admin-product-acc__title"]}>
              Product Access
            </h2>
            <p className={styles["admin-product-acc__subtitle"]}>
              Assign product applications to organizations and keep access
              aligned.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* <Button
            text="Refresh Data"
            icon={RefreshCw}
            onClick={loadProductAccess}
            disabled={loading}
            style={{
              backgroundColor: "#f8fafc",
              color: "#334155",
              boxShadow: "none",
            }} // Secondary style
          /> */}
          {isSuperAdmin &&
            (can("product-access", "feature-update-create") ||
              can("product-access", "feature-update-update") ||
              can("product-access", "feature-update-delete") ||
              can("product-access", "feature-update-publish")) && (
              <Button
                text="Features"
                icon={Megaphone}
                onClick={() => navigate("/admin/product-access/features")}
                style={{
                  backgroundColor: "#f1f5f9",
                  color: "#334155",
                  boxShadow: "none",
                }}
              />
            )}
          {can("product-access", "create") && (
            <Button
              text="Add New Product"
              icon={Plus}
              onClick={() => navigate("/admin/product-access/add-product")}
            />
          )}
        </div>
      </div>

      {/* STATS GRID */}
      <div className={styles["admin-product-acc__stats-grid"]}>
        <div className={styles["admin-product-acc__stat-card"]}>
          <div className={styles["admin-product-acc__stat-icon"]}>
            <Building2 size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-product-acc__stat-content"]}>
            <span className={styles["admin-product-acc__stat-label"]}>
              Organizations
            </span>
            <strong className={styles["admin-product-acc__stat-value"]}>
              {organizations.length}
            </strong>
          </div>
        </div>

        <div className={styles["admin-product-acc__stat-card"]}>
          <div className={styles["admin-product-acc__stat-icon"]}>
            <Package size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-product-acc__stat-content"]}>
            <span className={styles["admin-product-acc__stat-label"]}>
              Available Products
            </span>
            <strong className={styles["admin-product-acc__stat-value"]}>
              {products.length}
            </strong>
          </div>
        </div>

        <div className={styles["admin-product-acc__stat-card"]}>
          <div className={styles["admin-product-acc__stat-icon"]}>
            <Layers size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-product-acc__stat-content"]}>
            <span className={styles["admin-product-acc__stat-label"]}>
              Total Assigned Access
            </span>
            <strong className={styles["admin-product-acc__stat-value"]}>
              {totalAssigned}
            </strong>
          </div>
        </div>
      </div>

      {/* MAIN CARD (TABLE WRAPPER) */}
      <div className={styles["admin-product-acc__main-card"]}>
        <div className={styles["admin-product-acc__toolbar"]}>
          <div className={styles["admin-product-acc__search-box"]}>
            <Input
              type="search"
              placeholder="Search organization by name or email..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <TableSkeleton columns={5} rows={5} />
        ) : filteredOrganizations.length === 0 ? (
          <div className={styles["admin-product-acc__empty"]}>
            No organizations found.
          </div>
        ) : (
          <div className={styles["admin-product-acc__table-wrapper"]}>
            <table className={styles["admin-product-acc__table"]}>
              <thead>
                <tr>
                  <th className={styles["admin-product-acc__th"]}>
                    Organization
                  </th>
                  <th className={styles["admin-product-acc__th"]}>
                    Status & Contract
                  </th>
                  <th
                    className={styles["admin-product-acc__th"]}
                    style={{ minWidth: "400px" }}
                  >
                    Assigned Products
                  </th>
                  <th
                    className={styles["admin-product-acc__th"]}
                    style={{ minWidth: "500px" }}
                  >
                    Assigned APIs
                  </th>
                  <th className={styles["admin-product-acc__th"]}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrganizations.map((organization) => {
                  const selectedIds = toNumberArray(
                    assignedProducts[organization.id],
                  );
                  const dirty = isOrganizationDirty(organization.id);
                  const saving = savingOrgId === organization.id;

                  // Status Badge Class
                  let statusClass =
                    styles["admin-product-acc__badge--inactive"];
                  if (organization.status?.toLowerCase() === "active")
                    statusClass = styles["admin-product-acc__badge--active"];
                  if (organization.status?.toLowerCase() === "pending")
                    statusClass = styles["admin-product-acc__badge--pending"];

                  // Contract Badge Class
                  const contractClass = organization.contractSigned
                    ? styles["admin-product-acc__badge--signed"]
                    : styles["admin-product-acc__badge--unsigned"];

                  return (
                    <tr
                      key={organization.id}
                      className={styles["admin-product-acc__tr"]}
                    >
                      {/* ORGANIZATION CELL */}
                      <td className={styles["admin-product-acc__td"]}>
                        <div className={styles["admin-product-acc__org-cell"]}>
                          <div
                            className={styles["admin-product-acc__org-avatar"]}
                          >
                            {String(
                              organization.companyName ||
                                organization.name ||
                                "O",
                            )
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div
                            className={styles["admin-product-acc__org-meta"]}
                          >
                            <h3>
                              {organization.companyName ||
                                organization.name ||
                                "--"}
                            </h3>
                            <p>{organization.email || "--"}</p>
                          </div>
                        </div>
                      </td>

                      {/* BADGES CELL */}
                      <td className={styles["admin-product-acc__td"]}>
                        <div
                          className={styles["admin-product-acc__badges-cell"]}
                        >
                          <span
                            className={`${styles["admin-product-acc__badge"]} ${statusClass}`}
                          >
                            {organization.status || "--"}
                          </span>
                          <span
                            className={`${styles["admin-product-acc__badge"]} ${contractClass}`}
                          >
                            {organization.contractSigned
                              ? "Contract signed"
                              : "Contract pending"}
                          </span>
                        </div>
                      </td>

                      {/* PRODUCTS COMPACT PILLS CELL */}
                      <td className={styles["admin-product-acc__td"]}>
                        <div
                          className={styles["admin-product-acc__products-cell"]}
                        >
                          {products.map((product) => {
                            const checked = selectedIds.includes(product.id);
                            return (
                              <label
                                key={product.id}
                                className={`${styles["admin-product-acc__compact-toggle"]} ${
                                  checked
                                    ? styles[
                                        "admin-product-acc__compact-toggle--selected"
                                      ]
                                    : ""
                                }`}
                              >
                                {can("product-access", "grant-products") && (
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      toggleProduct(organization.id, product.id)
                                    }
                                  />
                                )}
                                {checked ? (
                                  <Check size={14} strokeWidth={3} />
                                ) : (
                                  <Plus size={14} strokeWidth={2.5} />
                                )}
                                {product.name}
                              </label>
                            );
                          })}
                        </div>
                      </td>

                      {/* ASSIGNED APIS CELL */}
                      <td className={styles["admin-product-acc__td"]}>
                        <div className={styles["admin-product-acc__apis-cell"]}>
                          {apiCategories.length === 0 ? (
                            <span
                              className={
                                styles["admin-product-acc__apis-empty"]
                              }
                            >
                              No APIs available
                            </span>
                          ) : (
                            apiCategories.map((category) => {
                              const categoryApiIds = (category.apis || []).map(
                                (api) => Number(api.id),
                              );
                              const selectedApiIdsForOrg = toNumberArray(
                                assignedApis[organization.id],
                              );
                              const selectedInCategoryCount =
                                categoryApiIds.filter((id) =>
                                  selectedApiIdsForOrg.includes(id),
                                ).length;

                              const isOpen =
                                openApiPopover?.orgId === organization.id &&
                                openApiPopover?.categoryId ===
                                  category.categoryId;

                              return (
                                <div
                                  key={category.categoryId}
                                  className={
                                    styles[
                                      "admin-product-acc__api-pill-wrapper"
                                    ]
                                  }
                                >
                                  <button
                                    type="button"
                                    data-api-pill
                                    className={`${styles["admin-product-acc__api-pill"]} ${
                                      selectedInCategoryCount > 0
                                        ? styles[
                                            "admin-product-acc__api-pill--has-selection"
                                          ]
                                        : ""
                                    } ${
                                      isOpen
                                        ? styles[
                                            "admin-product-acc__api-pill--open"
                                          ]
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setOpenApiPopover(
                                        isOpen
                                          ? null
                                          : {
                                              orgId: organization.id,
                                              categoryId: category.categoryId,
                                            },
                                      )
                                    }
                                  >
                                    <span>{category.categoryName}</span>
                                    <span
                                      className={
                                        styles[
                                          "admin-product-acc__api-pill-count"
                                        ]
                                      }
                                    >
                                      {selectedInCategoryCount} /{" "}
                                      {categoryApiIds.length}
                                    </span>
                                    <ChevronDown
                                      size={14}
                                      strokeWidth={2.5}
                                      className={
                                        isOpen
                                          ? styles[
                                              "admin-product-acc__chevron--open"
                                            ]
                                          : ""
                                      }
                                    />
                                  </button>

                                  {isOpen && (
                                    <div
                                      ref={apiPopoverRef}
                                      className={
                                        styles["admin-product-acc__api-popover"]
                                      }
                                    >
                                      <div
                                        className={
                                          styles[
                                            "admin-product-acc__api-popover-header"
                                          ]
                                        }
                                      >
                                        <Network size={14} strokeWidth={2.5} />
                                        <strong>{category.categoryName}</strong>
                                      </div>
                                      <ul
                                        className={
                                          styles[
                                            "admin-product-acc__api-popover-list"
                                          ]
                                        }
                                      >
                                        {(category.apis || []).map((api) => {
                                          const checked =
                                            selectedApiIdsForOrg.includes(
                                              Number(api.id),
                                            );
                                          return (
                                            <li key={api.id}>
                                              <label
                                                className={
                                                  styles[
                                                    "admin-product-acc__api-row"
                                                  ]
                                                }
                                              >
                                                {can(
                                                  "product-access",
                                                  "grant-apis",
                                                ) && (
                                                  <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() =>
                                                      toggleApi(
                                                        organization.id,
                                                        Number(api.id),
                                                      )
                                                    }
                                                  />
                                                )}
                                                <span
                                                  className={
                                                    styles[
                                                      "admin-product-acc__api-checkbox"
                                                    ]
                                                  }
                                                  aria-hidden
                                                >
                                                  {checked && (
                                                    <Check
                                                      size={12}
                                                      strokeWidth={3}
                                                    />
                                                  )}
                                                </span>
                                                <span
                                                  className={
                                                    styles[
                                                      "admin-product-acc__api-name"
                                                    ]
                                                  }
                                                >
                                                  {api.name}
                                                </span>
                                                {/* {api.code && (
                                                  <span
                                                    className={
                                                      styles[
                                                        "admin-product-acc__api-code"
                                                      ]
                                                    }
                                                  >
                                                    {api.code}
                                                  </span>
                                                )} */}
                                              </label>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </td>

                      {/* ACTIONS CELL */}
                      <td className={styles["admin-product-acc__td"]}>
                        <div
                          className={styles["admin-product-acc__action-cell"]}
                        >
                          {(can("product-access", "grant-products") ||
                            can("product-access", "grant-apis")) && (
                            <button
                              type="button"
                              className={`${styles["admin-product-acc__btn"]} ${
                                dirty
                                  ? styles["admin-product-acc__btn--primary"]
                                  : styles["admin-product-acc__btn--saved"]
                              }`}
                              onClick={() => {
                                if (!dirty || saving) return;
                                handleSave(organization);
                              }}
                              disabled={!dirty || saving}
                            >
                              {saving
                                ? "Saving..."
                                : dirty
                                  ? "Save changes"
                                  : "Saved"}
                            </button>
                          )}
                          <span>
                            {selectedIds.length} / {products.length} products
                          </span>
                          <span>
                            {
                              toNumberArray(assignedApis[organization.id])
                                .length
                            }{" "}
                            APIs assigned
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductAccess;
